import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { Alert, Box, Grid as Grid2, Stack, Typography } from '@mui/material';
import { format, subDays, subYears } from 'date-fns';

import type { GetOrganisationMonitoringQuery } from '@graphql/__generated__/graphql';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  getMonthsElapsed,
  getSellerContactsAvgPeriodStart,
} from '@modules/_shared/visibility-scoring';
import { EngagementScoreCard } from '@modules/organisations/OrganisationEngagementCard/EngagementScoreCard';
import { ENGAGEMENT_SIGNALS } from '@modules/organisations/OrganisationEngagementCard/engagementSignals';
import {
  AccountConnectionsCard,
  BillingAndContractCard,
  type EvolutionChart,
  RecentActivityCard,
  ScoreEvolutionCard,
  SentimentCard,
  VisibilityScoreCard,
  type VisibilityWindowDays,
  buildRecentActivity,
} from '@modules/organisations/OrganisationMonitoringCards';
import {
  GET_ORGANISATION_BILLING_SUMMARY,
  GET_ORGANISATION_ENGAGEMENT_SCORE,
  GET_ORGANISATION_MONITORING,
  GET_ORGANISATION_MONITORING_ACTIVITY,
  GET_ORGANISATION_POSTHOG_CSAT,
  GET_ORGANISATION_SELLER_SIGNALS,
  GET_ORGANISATION_VISIBILITY_SCORE,
} from '@modules/organisations/organisationsQueries';
import { presentVisibilityScoreFromAction } from '@modules/organisations/visibilityScoreFromAction';
import { useLocale } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';
import { getTenantHostname } from '@shared/utils/tenant';

/** The combined engagement-score trend uses a neutral dark line to stand apart from per-signal colors. */
const ENGAGEMENT_SCORE_CHART_COLOR = '#111827';

type Organisation = NonNullable<
  GetOrganisationMonitoringQuery['organisations_by_pk']
>;
type Team = Organisation['teams'][number];

const DEFAULT_VISIBILITY_WINDOW_DAYS: VisibilityWindowDays = 90;
const REVIEWS_KPI_DAYS = 90;
const POSTHOG_CSAT_DAYS = 90;
const POSTHOG_DATE_FORMAT = 'yyyy-MM-dd';

const hasService = (services: unknown) => {
  if (services == null || typeof services !== 'object') {
    return false;
  }
  return Object.values(services).some(Boolean);
};

const formatMoneyFromCents = ({
  amountCents,
  currency,
  locale,
  fractionDigits = 0,
}: {
  amountCents: number;
  currency: string;
  locale: string;
  fractionDigits?: number;
}) =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    currencyDisplay: 'code',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amountCents / 100);

const formatBillingDate = (date: string | null | undefined, locale: string) =>
  date == null
    ? null
    : new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(new Date(date));

const calculateTeamProfileCompletion = (team: Team) => {
  const checks = [
    Boolean(team.cover_photo?.url || team.cover_photo_image_id),
    Boolean(team.logo?.url || team.logo_image_id),
    Boolean(team.locality && team.country),
    Boolean(team.website_url),
    Boolean(team.phone_numbers[0]?.number),
    Boolean(team.active_since && team.active_since > 0),
    Boolean(team.description),
    team.teams_users.some(teamUser => hasService(teamUser.user?.services)),
    (team.property_transactions_aggregate.aggregate?.count ?? 0) >= 1,
  ];

  return Math.round(
    (checks.filter(Boolean).length / Math.max(checks.length, 1)) * 100,
  );
};

export const OrganisationMonitoring: React.FC<{ organisationId: string }> = ({
  organisationId,
}) => {
  const { t, locale, countryCode } = useLocale();
  const { me } = useAppData();
  const twoYearsAgoDate = useMemo(() => subYears(new Date(), 2), []);
  const [visibilityWindowDays, setVisibilityWindowDays] =
    useState<VisibilityWindowDays>(DEFAULT_VISIBILITY_WINDOW_DAYS);
  // Seller-signal Hasura aggregates share the same lookback as the visibility card.
  const visibilityWindowStart = useMemo(
    () => subDays(new Date(), visibilityWindowDays),
    [visibilityWindowDays],
  );
  const csatEndDate = useMemo(() => new Date(), []);
  const csatStartDate = useMemo(
    () => subDays(csatEndDate, POSTHOG_CSAT_DAYS - 1),
    [csatEndDate],
  );
  const { data, loading, error } = useQuery(GET_ORGANISATION_MONITORING, {
    variables: {
      id: organisationId,
      twoYearsAgo: twoYearsAgoDate.toISOString(),
    },
    // Revalidate on reopen so team membership changes made elsewhere show up.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const organisation = data?.organisations_by_pk;
  const sellerContactsAvgPeriodStartFallback = useMemo(
    () => subYears(new Date(), 1),
    [],
  );
  const {
    data: activityData,
    loading: activityLoading,
    error: activityError,
    refetch: refetchActivity,
  } = useQuery(GET_ORGANISATION_MONITORING_ACTIVITY, {
    variables: {
      id: organisationId,
      twoYearsAgo: twoYearsAgoDate.toISOString(),
      twoYearsAgoDate: format(twoYearsAgoDate, 'yyyy-MM-dd'),
      sellerContactsAvgPeriodStart:
        sellerContactsAvgPeriodStartFallback.toISOString(),
    },
    skip: organisation == null,
  });
  // Window-dependent seller signals live in their own query so switching the
  // visibility card's lookback only refetches these — not the whole dashboard.
  const { data: sellerSignalsData, loading: sellerSignalsLoading } = useQuery(
    GET_ORGANISATION_SELLER_SIGNALS,
    {
      variables: {
        id: organisationId,
        windowStart: visibilityWindowStart.toISOString(),
      },
      skip: organisation == null,
    },
  );
  const sellerContactsAvgPeriodStart = useMemo(() => {
    if (activityData?.property_transactions == null) {
      return sellerContactsAvgPeriodStartFallback;
    }
    return getSellerContactsAvgPeriodStart(activityData.property_transactions);
  }, [
    activityData?.property_transactions,
    sellerContactsAvgPeriodStartFallback,
  ]);
  useEffect(() => {
    if (
      organisation == null ||
      activityData?.property_transactions == null ||
      sellerContactsAvgPeriodStart.getTime() ===
        sellerContactsAvgPeriodStartFallback.getTime()
    ) {
      return;
    }

    void refetchActivity({
      sellerContactsAvgPeriodStart: sellerContactsAvgPeriodStart.toISOString(),
    });
  }, [
    activityData?.property_transactions,
    organisation,
    refetchActivity,
    sellerContactsAvgPeriodStart,
    sellerContactsAvgPeriodStartFallback,
  ]);
  const hostname = me?.tenant_id ? getTenantHostname(me.tenant_id) : null;
  const visibilityHostname = hostname ?? 'realadvisor.ch';
  const {
    data: visibilityScoreData,
    loading: visibilityScoreLoading,
    error: visibilityScoreError,
  } = useQuery(GET_ORGANISATION_VISIBILITY_SCORE, {
    variables: {
      organisationId,
      hostname: visibilityHostname,
      windowDays: visibilityWindowDays,
    },
    skip: organisation == null,
  });
  const {
    data: billingData,
    loading: billingLoading,
    error: billingError,
  } = useQuery(GET_ORGANISATION_BILLING_SUMMARY, {
    variables: {
      organisationId,
    },
    skip: organisation == null,
  });
  const {
    data: engagementScoreData,
    loading: engagementScoreLoading,
    error: engagementScoreError,
  } = useQuery(GET_ORGANISATION_ENGAGEMENT_SCORE, {
    variables: { organisationId },
    skip: organisation == null,
  });
  const {
    data: csatData,
    loading: csatLoading,
    error: csatError,
  } = useQuery(GET_ORGANISATION_POSTHOG_CSAT, {
    variables: {
      organisationId,
      startDate: format(csatStartDate, POSTHOG_DATE_FORMAT),
      endDate: format(csatEndDate, POSTHOG_DATE_FORMAT),
    },
    skip: organisation == null,
  });

  const billingSummary = billingData?.organisation_billing_summary;
  const csatSummary = csatData?.organisation_posthog_csat;
  const teamUsersById = useMemo(() => {
    const users = new Map<
      string,
      NonNullable<Organisation['teams'][number]['teams_users'][number]['user']>
    >();

    for (const team of organisation?.teams ?? []) {
      for (const teamUser of team.teams_users) {
        if (teamUser.user?.id != null) {
          users.set(teamUser.user.id, teamUser.user);
        }
      }
    }

    return users;
  }, [organisation]);
  const metrics = useMemo(() => {
    const teams = organisation?.teams ?? [];
    const reviewKpiStartDate = subDays(new Date(), REVIEWS_KPI_DAYS);
    const profileCompletionScores = teams.map(calculateTeamProfileCompletion);
    const averageProfileCompletion =
      profileCompletionScores.length > 0
        ? Math.round(
            profileCompletionScores.reduce((sum, score) => sum + score, 0) /
              profileCompletionScores.length,
          )
        : 0;

    return {
      reviewsTotalCount: teams.reduce(
        (acc, team) =>
          acc + (team.combined_reviews_aggregate.aggregate?.count ?? 0),
        0,
      ),
      reviewsLast90DaysCount:
        activityData?.combined_reviews.filter(
          review =>
            review.review_date != null &&
            new Date(review.review_date) >= reviewKpiStartDate,
        ).length ?? 0,
      transactionsCount: activityData?.property_transactions.length ?? 0,
      averageProfileCompletion,
    };
  }, [activityData, organisation]);

  const visibilityScorePresentation = useMemo(() => {
    const action = visibilityScoreData?.organisation_visibility_score;
    if (action == null) {
      return null;
    }
    return presentVisibilityScoreFromAction({ action, t });
  }, [visibilityScoreData?.organisation_visibility_score, t]);

  const visibilityScore = visibilityScorePresentation?.current ?? null;
  const visibilityScoreDelta = visibilityScorePresentation?.delta ?? 0;
  const visibilityScoreDeltaLabel = t('{{delta}} vs. previous period', {
    delta:
      visibilityScoreDelta > 0
        ? `+${visibilityScoreDelta}`
        : visibilityScoreDelta,
  });
  const visibilityScoreDeltaColor =
    visibilityScoreDelta > 0
      ? 'success.main'
      : visibilityScoreDelta < 0
      ? 'error.main'
      : 'text.secondary';
  const visibilityInfo = useMemo(() => {
    const positions = visibilityScore?.positionBreakdown ?? [];
    const totalPositionImpressions = positions.reduce(
      (sum, p) => sum + p.count,
      0,
    );
    const averagePosition =
      totalPositionImpressions === 0
        ? null
        : positions.reduce((sum, p) => sum + p.position * p.count, 0) /
          totalPositionImpressions;

    return {
      totalReviews: metrics.reviewsTotalCount,
      transactions24mo:
        visibilityScore?.transactionsCount24mo ?? metrics.transactionsCount,
      averagePosition,
      sizeMultiplier: visibilityScore?.sizeMultiplier ?? 1,
    };
  }, [
    metrics.reviewsTotalCount,
    metrics.transactionsCount,
    visibilityScore?.positionBreakdown,
    visibilityScore?.sizeMultiplier,
    visibilityScore?.transactionsCount24mo,
  ]);
  const visibilityAdminCalibration = useMemo(() => {
    const sellerContacts =
      sellerSignalsData?.seller_contacts_window?.aggregate?.count ?? 0;
    const sellerEnquiries =
      sellerSignalsData?.seller_enquiries_window?.aggregate?.count ?? 0;
    const sellerContactsInPeriod =
      activityData?.seller_contacts_avg_period?.aggregate?.count ?? 0;
    const monthsElapsed = getMonthsElapsed(sellerContactsAvgPeriodStart);

    return {
      sellerContacts,
      sellerEnquiries,
      phoneClicks: visibilityScore?.phoneClicksTotal ?? 0,
      avgSellerContactsPerMonth: sellerContactsInPeriod / monthsElapsed,
    };
  }, [
    sellerSignalsData?.seller_contacts_window?.aggregate?.count,
    sellerSignalsData?.seller_enquiries_window?.aggregate?.count,
    activityData?.seller_contacts_avg_period?.aggregate?.count,
    sellerContactsAvgPeriodStart,
    visibilityScore?.phoneClicksTotal,
  ]);

  const engagementScore =
    engagementScoreData?.organisation_engagement_score ?? null;

  const recentActivityItems = useMemo(
    () =>
      buildRecentActivity({
        reviews: activityData?.combined_reviews ?? [],
        transactions: activityData?.property_transactions ?? [],
        organisationName: organisation?.name ?? '',
        t,
      }),
    [activityData, organisation?.name, t],
  );

  const formatBillingMoney = (amountCents: number, fractionDigits = 0) =>
    formatMoneyFromCents({
      amountCents,
      currency:
        billingSummary?.currency ?? getCurrencyByCountryCode(countryCode),
      locale,
      fractionDigits,
    });

  const formatMonitoringDate = useCallback(
    (date: string | null | undefined) => formatBillingDate(date, locale),
    [locale],
  );

  const engagementScorePending =
    engagementScoreLoading && engagementScoreData == null;

  const evolutionCharts = useMemo<EvolutionChart[]>(() => {
    if (engagementScore == null) {
      return [];
    }

    // One point per month: dbt month-end snapshots, then today's score.
    const pointsByMonth = new Map(
      engagementScore.history.map(point => [point.date.slice(0, 7), point]),
    );
    const points = [...pointsByMonth.entries()];
    const toChart = (
      label: string,
      color: string,
      values: { label: string; value: number }[],
    ): EvolutionChart => {
      const last = values[values.length - 1]?.value ?? 0;
      const delta = last - (values[values.length - 2]?.value ?? last);
      return {
        label,
        value: `${last}%`,
        deltaLabel: delta === 0 ? '—' : delta > 0 ? `+${delta}` : `${delta}`,
        color,
        data: values,
      };
    };

    const charts = [
      toChart(
        t('Engagement score'),
        ENGAGEMENT_SCORE_CHART_COLOR,
        points.map(([month, point]) => ({ label: month, value: point.score })),
      ),
    ];
    for (const { key, label, color } of ENGAGEMENT_SIGNALS) {
      const counted = engagementScore.signals.find(
        signal => signal.key === key,
      )?.counted;
      if (counted !== true) {
        continue;
      }
      charts.push(
        toChart(
          label(t),
          color,
          points.flatMap(([month, point]) => {
            const value = point[key];
            return value == null ? [] : [{ label: month, value }];
          }),
        ),
      );
    }
    return charts;
  }, [engagementScore, t]);

  const monitoringTopLoading = activityLoading || billingLoading;

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  if (loading || organisation == null) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>{t('Loading...')}</Typography>
      </Box>
    );
  }

  const monitoringGridItemSx = {
    minWidth: 0,
    display: 'flex',
    '& > *': {
      flex: 1,
      minWidth: 0,
      width: '100%',
    },
  };

  return (
    <Stack spacing={2} sx={{ p: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
      <Grid2 container spacing={2} columns={12}>
        <Grid2 size={{ xs: 12, sm: 12, lg: 12 }} sx={monitoringGridItemSx}>
          <AccountConnectionsCard
            organisation={organisation}
            billingSummary={billingSummary}
            loading={monitoringTopLoading}
            formatDate={formatMonitoringDate}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, lg: 3 }} sx={monitoringGridItemSx}>
          <EngagementScoreCard
            engagementScore={engagementScore}
            loading={engagementScorePending}
            error={engagementScoreError != null}
            formatDate={formatMonitoringDate}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, lg: 3 }} sx={monitoringGridItemSx}>
          <VisibilityScoreCard
            score={visibilityScore?.score ?? 0}
            result={visibilityScore}
            deltaLabel={visibilityScoreDeltaLabel}
            deltaColor={visibilityScoreDeltaColor}
            showAdminCalibration={me?.can_edit_settings === true}
            adminCalibration={visibilityAdminCalibration}
            info={visibilityInfo}
            windowDays={visibilityWindowDays}
            onWindowDaysChange={setVisibilityWindowDays}
            loading={
              visibilityScoreLoading || activityLoading || sellerSignalsLoading
            }
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, lg: 3 }} sx={monitoringGridItemSx}>
          <SentimentCard
            csatSummary={csatSummary}
            teamUsersById={teamUsersById}
            loading={csatLoading}
            error={csatError?.message}
            formatDate={formatMonitoringDate}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, lg: 3 }} sx={monitoringGridItemSx}>
          <BillingAndContractCard
            billingSummary={billingSummary}
            formatMoney={formatBillingMoney}
            formatDate={formatMonitoringDate}
            loading={monitoringTopLoading}
            billingError={billingError?.message}
          />
        </Grid2>
      </Grid2>

      <Grid2 container spacing={2} columns={12}>
        <Grid2 size={{ xs: 12, md: 8 }} sx={monitoringGridItemSx}>
          <ScoreEvolutionCard
            charts={evolutionCharts}
            loading={engagementScorePending}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }} sx={monitoringGridItemSx}>
          <RecentActivityCard
            items={recentActivityItems}
            loading={activityLoading}
          />
        </Grid2>
      </Grid2>

      {visibilityScoreError && (
        <Alert severity="warning">
          {t('Could not load visibility analytics.')}
          {visibilityScoreError.message
            ? ` ${visibilityScoreError.message}`
            : null}
        </Alert>
      )}
      {activityError && (
        <Alert severity="warning">
          {t('Could not load customer activity.')}
        </Alert>
      )}
    </Stack>
  );
};
